import type { GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { uploadEntityAttachments, fetchEntityAttachments, deleteAttachment } from '@/lib/entity-attachments';
import { fetchEntity, getJson, writeEntity } from '@/lib/entity-api';

// Material Order attachments: the shared implementation (lib/entity-attachments.ts)
// -- this file only names the entity type these files belong to.
const ENTITY_TYPE = 'material_list';

export type MaterialOrderFileAttachment = GenericAttachment;

export function uploadMaterialOrderAttachments(parentId: string | undefined, files: File[], companyId: string, userId: string): Promise<MaterialOrderFileAttachment[]> {
  return uploadEntityAttachments(ENTITY_TYPE, parentId, files, companyId, userId);
}

export function fetchMaterialOrderAttachments(parentId: string, opts?: { ascending?: boolean }): Promise<MaterialOrderFileAttachment[]> {
  return fetchEntityAttachments(ENTITY_TYPE, parentId, opts);
}

export function deleteMaterialOrderAttachment(attachmentId: string, userId: string | undefined): Promise<boolean> {
  return deleteAttachment(attachmentId, userId);
}

/**
 * The items of a material order, in the generic line shape (GET /api/material-orders/:id/lines): the same shape a purchase order's items have, so
 * `lineFromRow` reads them. Rejects when they cannot be read -- the form then refuses to save them, never saving an empty list over the real one.
 */
export function fetchMaterialOrderLines(orderId: string): Promise<Array<Record<string, any>>> {
  return getJson<Array<Record<string, any>>>(`api/material-orders/${orderId}/lines`, 'Failed to load the items of the material order');
}

/**
 * Saves ALL the items of a material order at once (PUT /api/material-orders/:id/lines: validated and all-or-nothing); the server sets the order's total
 * from them + its delivery, tax and other. Idempotent: the same list saved twice leaves the same items.
 */
export function saveMaterialOrderLines(orderId: string, lines: Array<Record<string, unknown>>): Promise<{ items: Array<Record<string, any>>; total_cost: number }> {
  return writeEntity(`api/material-orders/${orderId}/lines`, 'PUT', { lines }, 'Failed to save the items of the material order');
}

/** One material order by id, as the list shows it (a link from another document opens it); undefined when it no longer exists or the caller cannot read it. */
export async function fetchMaterialOrderById(id: string): Promise<Record<string, any> | undefined> {
  try {
    return (await fetchEntity<Record<string, any>>('material_list', id, { includeCounts: true })) || undefined;
  } catch (failure) {
    console.error('Error loading the material order:', failure);
    return undefined;
  }
}
